import { useState } from 'react'
import { useQuery } from 'react-query'
import { leaderboardAPI, challengesAPI } from '../services/api'
import { 
  TrophyIcon, 
  UserGroupIcon,
  GlobeAltIcon,
  ChartBarIcon,
  StarIcon,
  FireIcon
} from '@heroicons/react/24/outline'
import { TrophyIcon as TrophyIconSolid } from '@heroicons/react/24/solid'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ImageWithFallback from '../components/ui/ImageWithFallback'
import { getPlaceholderImage, getLocalImage, heroImages } from '../utils/images'
import clsx from 'clsx'

const tabs = [
  { id: 'global', name: 'Global Leaderboard', icon: GlobeAltIcon },
  { id: 'challenges', name: 'By Challenge', icon: ChartBarIcon },
  { id: 'categories', name: 'By Category', icon: UserGroupIcon },
]

const getRankIcon = (rank) => {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return `#${rank}`
}

const getRankColor = (rank) => {
  if (rank === 1) return 'text-gold-400'
  if (rank === 2) return 'text-gray-400'
  if (rank === 3) return 'text-gold-500'
  return 'text-gray-400'
}

export default function LeaderboardPage() {
  const [activeTab, setActiveTab] = useState('global')
  const [selectedChallenge, setSelectedChallenge] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  // Global leaderboard
  const { data: globalData, isLoading: globalLoading } = useQuery(
    ['leaderboard', 'global'],
    () => leaderboardAPI.getGlobal({ limit: 50 }),
    {
      select: (response) => response.data,
      enabled: Boolean(activeTab === 'global')
    }
  )

  // Challenges for dropdown
  const { data: challengesData } = useQuery(
    'challenges-for-leaderboard',
    () => challengesAPI.getAll({ status: 'completed' }),
    {
      select: (response) => response.data.challenges,
      enabled: Boolean(activeTab === 'challenges')
    }
  )

  // Challenge leaderboard
  const { data: challengeData, isLoading: challengeLoading } = useQuery(
    ['leaderboard', 'challenge', selectedChallenge],
    () => leaderboardAPI.getByChallenge(selectedChallenge, { limit: 50 }),
    {
      select: (response) => response.data,
      enabled: Boolean(activeTab === 'challenges' && selectedChallenge)
    }
  )

  // Categories
  const { data: categoriesData } = useQuery(
    'challenge-categories',
    () => challengesAPI.getCategories(),
    {
      select: (response) => response.data.categories,
      enabled: Boolean(activeTab === 'categories')
    }
  )

  // Category leaderboard
  const { data: categoryData, isLoading: categoryLoading } = useQuery(
    ['leaderboard', 'category', selectedCategory],
    () => leaderboardAPI.getByCategory(selectedCategory, { limit: 50 }),
    {
      select: (response) => response.data,
      enabled: Boolean(activeTab === 'categories' && selectedCategory)
    }
  )

  const renderGlobalLeaderboard = () => {
    if (globalLoading) {
      return (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )
    }

    if (!globalData?.globalLeaderboard?.length) {
      return (
        <div className="text-center py-12">
          <TrophyIcon className="mx-auto h-12 w-12 text-gray-400 animate-float" />
          <h3 className="mt-2 text-sm font-medium text-gray-100">No rankings yet</h3>
          <p className="mt-1 text-sm text-gray-400">
            Be the first to submit a solution and appear on the leaderboard!
          </p>
        </div>
      )
    }

    return (
      <div className="bg-dark-800 shadow-xl overflow-hidden sm:rounded-md border border-primary-500/20 gold-corner-accent">
        <ul className="divide-y divide-dark-700">
          {globalData.globalLeaderboard.map((entry, index) => (
            <li key={entry.user.id} className="px-6 py-4 hover:bg-dark-700 transition-colors duration-200 hover-gold-lift" style={{animationDelay: `${index * 0.1}s`}}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shadow-glow ${
                    entry.rank === 1 ? 'bg-gradient-gold text-dark-900 animate-gold-pulse' :
                    entry.rank === 2 ? 'bg-gray-600 text-gray-100' :
                    entry.rank === 3 ? 'bg-gold-600 text-dark-900' :
                    'bg-primary-600 text-gray-100'
                  }`}>
                    {entry.rank <= 3 ? getRankIcon(entry.rank) : `#${entry.rank}`}
                  </div>
                  <div className="ml-4 flex items-center">
                    <ImageWithFallback
                      src={getLocalImage('experts')}
                      alt={entry.user.username || entry.user.displayName || `user_${entry.user.id}`}
                      className="h-10 w-10 rounded-full object-cover border-2 border-primary-500/30"
                      fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23f59e0b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-100 flex items-center">
                        {entry.user.username || entry.user.displayName || `user_${entry.user.id}`}
                        {entry.rank <= 3 && (
                          <StarIcon className="ml-2 h-4 w-4 text-gold-400 animate-gold-pulse" />
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {entry.user.organization && `${entry.user.organization} • `}
                        {entry.user.country}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center">
                    <div className="mr-4">
                      <div className="text-sm font-medium text-gray-100">
                        Avg Score: <span className="text-gold-400">{entry.stats.averageScore}</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        {entry.stats.challengesParticipated} challenges • {entry.stats.totalSubmissions} submissions
                      </div>
                    </div>
                    {entry.rank <= 10 && (
                      <FireIcon className="h-5 w-5 text-gold-400 animate-gold-pulse" />
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderChallengeLeaderboard = () => {
    if (!selectedChallenge) {
      return (
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Select a challenge</h3>
          <p className="mt-1 text-sm text-gray-500">
            Choose a challenge from the dropdown to view its leaderboard.
          </p>
        </div>
      )
    }

    if (challengeLoading) {
      return (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )
    }

    if (!challengeData?.leaderboard?.length) {
      return (
        <div className="text-center py-12">
          <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Be the first to submit a solution for this challenge!
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="bg-primary-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-primary-900">
            {challengeData.challenge.title}
          </h3>
          <p className="text-sm text-primary-700">
            {challengeData.challenge.category} • {challengeData.challenge.difficulty}
          </p>
        </div>
        
        <div className="bg-dark-800 shadow-xl overflow-hidden sm:rounded-md border border-primary-500/20 gold-corner-accent">
          <ul className="divide-y divide-dark-700">
            {challengeData.leaderboard.map((entry, index) => (
              <li key={entry.id} className="px-6 py-4 hover:bg-dark-700 transition-colors duration-200 hover-gold-lift" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 w-8 text-center font-bold ${getRankColor(entry.rank)} ${entry.rank <= 3 ? 'animate-gold-pulse' : ''}`}>
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-100">
                        {entry.username || entry.displayName || `user_${entry.id}`}
                      </div>
                      <div className="text-sm text-gray-400">
                        {entry.organization && `${entry.organization} • `}
                        {entry.country}
                      </div>
                      <div className="text-sm text-primary-400 font-medium">
                        {entry.title}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gold-400">
                      {entry.score}
                    </div>
                    <div className="text-sm text-gray-400">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  const renderCategoryLeaderboard = () => {
    if (!selectedCategory) {
      return (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Select a category</h3>
          <p className="mt-1 text-sm text-gray-500">
            Choose a category from the dropdown to view its leaderboard.
          </p>
        </div>
      )
    }

    if (categoryLoading) {
      return (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )
    }

    if (!categoryData?.categoryLeaderboard?.length) {
      return (
        <div className="text-center py-12">
          <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            No evaluated submissions in this category yet.
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <div className="bg-secondary-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-secondary-900">
            {selectedCategory} Category
          </h3>
          <p className="text-sm text-secondary-700">
            Top performers across all challenges in this category
          </p>
        </div>
        
        <div className="bg-dark-800 shadow-xl overflow-hidden sm:rounded-md border border-primary-500/20 gold-corner-accent">
          <ul className="divide-y divide-dark-700">
            {categoryData.categoryLeaderboard.map((entry, index) => (
              <li key={`${entry.user.id}-${entry.challenge.id}`} className="px-6 py-4 hover:bg-dark-700 transition-colors duration-200 hover-gold-lift" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 w-8 text-center font-bold ${getRankColor(entry.rank)} ${entry.rank <= 3 ? 'animate-gold-pulse' : ''}`}>
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-100">
                        {entry.user.username || entry.user.displayName || `user_${entry.user.id}`}
                      </div>
                      <div className="text-sm text-gray-400">
                        {entry.user.organization && `${entry.user.organization} • `}
                        {entry.user.country}
                      </div>
                      <div className="text-sm text-primary-400 font-medium">
                        {entry.challenge.title}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gold-400">
                      {entry.score}
                    </div>
                    <div className="text-sm text-gray-400">
                      {new Date(entry.submittedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-dark-900 min-h-screen">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-dark-800 to-dark-900 overflow-hidden">
        <div className="absolute inset-0">
          <ImageWithFallback
            src={heroImages.leaderboard}
            alt="Leaderboard background"
            className="w-full h-full object-cover opacity-20"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 400'%3E%3Crect width='1200' height='400' fill='%23334155'/%3E%3C/svg%3E"
          />
        </div>
        <div className="absolute inset-0 bg-dark-pattern opacity-30"></div>
        <div className="relative max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-gold bg-opacity-20 rounded-full backdrop-blur-sm shadow-glow animate-gold-pulse">
                <TrophyIconSolid className="h-12 w-12 text-gold-400" />
              </div>
            </div>
            <h1 className="text-4xl font-extrabold text-gray-100 sm:text-5xl lg:text-6xl relative">
              <span className="text-gradient-gold gold-text-shadow">Leaderboard</span>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h1>
            <p className="mt-8 max-w-2xl mx-auto text-xl text-gray-300">
              See how you rank against other participants in AI challenges. Compete with the best minds from around the world.
            </p>
            <div className="mt-8 flex justify-center space-x-8">
              <div className="text-center hover-gold-lift">
                <div className="text-3xl font-bold text-gradient-gold gold-text-shadow animate-gold-pulse">10,000+</div>
                <div className="text-gray-400">Participants</div>
              </div>
              <div className="text-center hover-gold-lift" style={{animationDelay: '0.2s'}}>
                <div className="text-3xl font-bold text-gradient-gold gold-text-shadow animate-gold-pulse" style={{animationDelay: '0.2s'}}>50+</div>
                <div className="text-gray-400">Countries</div>
              </div>
              <div className="text-center hover-gold-lift" style={{animationDelay: '0.4s'}}>
                <div className="text-3xl font-bold text-gradient-gold gold-text-shadow animate-gold-pulse" style={{animationDelay: '0.4s'}}>$500K+</div>
                <div className="text-gray-400">Prize Pool</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-dark-600 bg-dark-800 text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md transition-colors duration-200"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.name}
                </option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="flex space-x-8">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center px-1 py-2 border-b-2 font-medium text-sm transition-all duration-200 hover-gold-lift',
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-400 gold-text-shadow'
                      : 'border-transparent text-gray-400 hover:text-primary-400 hover:border-primary-500/50'
                  )}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <tab.icon className="mr-2 h-5 w-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Filters */}
        {activeTab === 'challenges' && (
          <div className="mb-6 bg-dark-800 p-4 rounded-lg shadow-xl border border-primary-500/20 gold-corner-accent">
            <div className="max-w-xs">
              <label htmlFor="challenge-select" className="block text-sm font-medium text-gray-300 mb-2">
                Select Challenge
              </label>
              <select
                id="challenge-select"
                value={selectedChallenge}
                onChange={(e) => setSelectedChallenge(e.target.value)}
                className="block w-full px-3 py-2 border border-dark-600 rounded-md bg-dark-700 text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200"
              >
                <option value="">Choose a challenge...</option>
                {challengesData?.map((challenge) => (
                  <option key={challenge.id} value={challenge.id}>
                    {challenge.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="mb-6 bg-dark-800 p-4 rounded-lg shadow-xl border border-primary-500/20 gold-corner-accent">
            <div className="max-w-xs">
              <label htmlFor="category-select" className="block text-sm font-medium text-gray-300 mb-2">
                Select Category
              </label>
              <select
                id="category-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full px-3 py-2 border border-dark-600 rounded-md bg-dark-700 text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200"
              >
                <option value="">Choose a category...</option>
                {categoriesData?.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Content */}
        <div>
          {activeTab === 'global' && renderGlobalLeaderboard()}
          {activeTab === 'challenges' && renderChallengeLeaderboard()}
          {activeTab === 'categories' && renderCategoryLeaderboard()}
        </div>
      </div>
    </div>
  )
}